// Ansoku core game engine — pure logic, no rendering. Faithful to the Unity mechanics:
//  - 10x10 board, a 3-piece drawer; place all 3 before a new drawer is dealt.
//  - Each piece's cubes share value floor(cellCount/2), min 1.
//  - Placing a piece adds each cube's value to the EXISTING board cubes it touches
//    (siblings within the same piece do not boost each other).
//  - A full row or column clears, banking the sum of its cubes' values.
//  - Clearing 2+ lines at once applies an increasing multiplier.
//  - Rotation points: start with 1; each cleared cube charges +1; at 100 you earn another.
//    Spend a point to enable rotating one drawer piece.
//  - One-level undo of the last placement (or rotation-point spend) within the current drawer.
//  - No remaining drawer piece fits anywhere -> game over.

import {
  BOARD_SIZE,
  TRAY_SLOTS,
  ROTATE_REQUIREMENT,
  START_ROTATES,
  type SaveState,
  type SavedTrayPiece,
} from '@shared/types';
import {
  SHAPES,
  shapeValue,
  rotateCells,
  cellsExtent,
  type ShapeDef,
  type Cell,
} from './shapes';
import { mathRandomRNG, type RNG } from './rng';

const N = BOARD_SIZE;

export interface TrayPiece {
  shapeIndex: number;
  value: number;
  rotation: number; // 0 | 90 | 180 | 270
  rotationEnabled: boolean;
  cells: Cell[]; // current rotated + normalized
}

export interface Boost {
  index: number; // board index of an existing cube that gained value
  amount: number; // total value added
  newValue: number;
}

export interface ClearedCube {
  index: number;
  value: number;
}

export interface ClearedLine {
  type: 'row' | 'col';
  index: number; // row z, or col x
}

export interface PlaceResult {
  slot: number;
  placed: number[]; // board indices that received new cubes
  placedValue: number;
  boosts: Boost[];
  cleared: ClearedCube[];
  clearedLines: ClearedLine[];
  baseScore: number;
  bonusScore: number;
  scoreGained: number;
  newScore: number;
  rotatePointsGained: number;
  newHighScore: boolean;
  trayEmpty: boolean;
  gameOver: boolean;
}

export interface EngineOptions {
  shapes?: ShapeDef[];
  rng?: RNG;
  highScore?: number;
}

export class AnsokuGame {
  readonly shapes: ShapeDef[];
  private rng: RNG;

  board: number[] = new Array(N * N).fill(0);
  tray: (TrayPiece | null)[] = [null, null, null];
  score = 0;
  highScore = 0;
  workingOnHighScore = false;
  rotates = START_ROTATES;
  rotateCharge = 0;
  gameOver = false;
  gameInProgress = false;

  private undoSnapshot: SaveState | null = null;
  undoAvailable = false;

  constructor(opts: EngineOptions = {}) {
    this.shapes = opts.shapes ?? SHAPES;
    this.rng = opts.rng ?? mathRandomRNG();
    this.highScore = opts.highScore ?? 0;
  }

  // ---- helpers ----
  static idx(x: number, z: number): number {
    return z * N + x;
  }
  private inBounds(x: number, z: number): boolean {
    return x >= 0 && x < N && z >= 0 && z < N;
  }

  makePiece(shapeIndex: number, rotation = 0, rotationEnabled = false): TrayPiece {
    const def = this.shapes[shapeIndex];
    const steps = (((rotation / 90) % 4) + 4) % 4;
    const cells = rotateCells(def.cells, steps);
    return {
      shapeIndex,
      value: shapeValue(def.cells.length),
      rotation: ((rotation % 360) + 360) % 360,
      rotationEnabled,
      cells,
    };
  }

  // ---- lifecycle ----
  newGame(): void {
    this.board.fill(0);
    this.score = 0;
    this.workingOnHighScore = false;
    this.rotates = START_ROTATES;
    this.rotateCharge = 0;
    this.gameOver = false;
    this.gameInProgress = true;
    this.undoSnapshot = null;
    this.undoAvailable = false;
    this.dealDrawer();
  }

  /** Deal 3 distinct weighted-random shapes, each with a random orientation. */
  dealDrawer(): void {
    const used = new Set<number>();
    const newTray: (TrayPiece | null)[] = [];
    for (let i = 0; i < TRAY_SLOTS; i++) {
      const shapeIndex = this.pickShapeIndex(used);
      used.add(shapeIndex);
      const rotation = [0, 90, 180, 270][this.rng.int(4)];
      newTray.push(this.makePiece(shapeIndex, rotation, false));
    }
    this.tray = newTray;
    this.undoSnapshot = null;
    this.undoAvailable = false;
    // Game over if none of the freshly dealt pieces fits.
    if (!this.hasAnyValidMove()) this.gameOver = true;
  }

  private pickShapeIndex(exclude: Set<number>): number {
    const pool = this.shapes
      .map((s, i) => ({ i, chance: Math.max(1, s.chance) }))
      .filter((e) => !exclude.has(e.i));
    const total = pool.reduce((a, e) => a + e.chance, 0);
    let r = this.rng.next() * total;
    for (const e of pool) {
      r -= e.chance;
      if (r <= 0) return e.i;
    }
    return pool[pool.length - 1].i;
  }

  // ---- placement ----
  canPlaceCells(cells: Cell[], ax: number, az: number): boolean {
    for (const cell of cells) {
      const x = ax + cell.x;
      const z = az + cell.z;
      if (!this.inBounds(x, z)) return false;
      if (this.board[AnsokuGame.idx(x, z)] !== 0) return false;
    }
    return true;
  }

  /** Place the piece in `slot` with its top-left at board cell (ax, az). Returns null if invalid. */
  placePiece(slot: number, ax: number, az: number): PlaceResult | null {
    const piece = this.tray[slot];
    if (!piece || this.gameOver) return null;
    if (!this.canPlaceCells(piece.cells, ax, az)) return null;

    // Snapshot for one-level undo (state before this placement).
    this.undoSnapshot = this.toSave();
    this.undoAvailable = true;

    const placedSet = new Set<number>();
    const placed: number[] = [];
    for (const cell of piece.cells) {
      const i = AnsokuGame.idx(ax + cell.x, az + cell.z);
      this.board[i] = piece.value;
      placedSet.add(i);
      placed.push(i);
    }

    // Merge: each placed cube adds its value to existing (non-sibling) orthogonal neighbours.
    const boostMap = new Map<number, number>();
    const dirs = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ];
    for (const cell of piece.cells) {
      const px = ax + cell.x;
      const pz = az + cell.z;
      for (const [dx, dz] of dirs) {
        const nx = px + dx;
        const nz = pz + dz;
        if (!this.inBounds(nx, nz)) continue;
        const ni = AnsokuGame.idx(nx, nz);
        if (placedSet.has(ni)) continue;
        if (this.board[ni] > 0) {
          this.board[ni] += piece.value;
          boostMap.set(ni, (boostMap.get(ni) ?? 0) + piece.value);
        }
      }
    }
    const boosts: Boost[] = [...boostMap.entries()].map(([index, amount]) => ({
      index,
      amount,
      newValue: this.board[index],
    }));

    this.tray[slot] = null;

    // Line clears.
    const { clearedLines, clearedSet } = this.findClears();
    const cleared: ClearedCube[] = [];
    let baseScore = 0;
    for (const i of clearedSet) {
      cleared.push({ index: i, value: this.board[i] });
      baseScore += this.board[i];
    }

    const linesCleared = clearedLines.length;
    let bonusScore = 0;
    let scoreGained = baseScore;
    if (linesCleared >= 2) {
      const multiplier = 1 + 0.1 * linesCleared;
      scoreGained = Math.round(baseScore * multiplier);
      bonusScore = scoreGained - baseScore;
    }

    // Remove cleared cubes and bank rotation charges (one per cleared cube).
    let rotatePointsGained = 0;
    for (const i of clearedSet) {
      this.board[i] = 0;
      this.rotateCharge++;
      if (this.rotateCharge >= ROTATE_REQUIREMENT) {
        this.rotateCharge = 0;
        this.rotates++;
        rotatePointsGained++;
      }
    }

    this.score += scoreGained;

    let newHighScore = false;
    if (this.score > this.highScore) {
      this.highScore = this.score;
      if (!this.workingOnHighScore) {
        this.workingOnHighScore = true;
        newHighScore = true;
      }
    }

    const trayEmpty = this.tray.every((p) => p === null);
    // Game over only checked while pieces remain in the drawer (Unity: activeAltars != 0).
    if (!trayEmpty && !this.hasAnyValidMove()) this.gameOver = true;

    return {
      slot,
      placed,
      placedValue: piece.value,
      boosts,
      cleared,
      clearedLines,
      baseScore,
      bonusScore,
      scoreGained,
      newScore: this.score,
      rotatePointsGained,
      newHighScore,
      trayEmpty,
      gameOver: this.gameOver,
    };
  }

  private findClears(): { clearedLines: ClearedLine[]; clearedSet: Set<number> } {
    const clearedLines: ClearedLine[] = [];
    const clearedSet = new Set<number>();
    // Rows
    for (let z = 0; z < N; z++) {
      let full = true;
      for (let x = 0; x < N; x++) {
        if (this.board[AnsokuGame.idx(x, z)] === 0) {
          full = false;
          break;
        }
      }
      if (full) {
        clearedLines.push({ type: 'row', index: z });
        for (let x = 0; x < N; x++) clearedSet.add(AnsokuGame.idx(x, z));
      }
    }
    // Columns
    for (let x = 0; x < N; x++) {
      let full = true;
      for (let z = 0; z < N; z++) {
        if (this.board[AnsokuGame.idx(x, z)] === 0) {
          full = false;
          break;
        }
      }
      if (full) {
        clearedLines.push({ type: 'col', index: x });
        for (let z = 0; z < N; z++) clearedSet.add(AnsokuGame.idx(x, z));
      }
    }
    return { clearedLines, clearedSet };
  }

  // ---- rotation ----
  /** Spend a rotation point to enable rotating the piece in `slot`. */
  enableRotation(slot: number): boolean {
    const piece = this.tray[slot];
    if (!piece || piece.rotationEnabled || this.rotates <= 0 || this.gameOver) return false;
    this.undoSnapshot = this.toSave();
    this.undoAvailable = true;
    this.rotates--;
    piece.rotationEnabled = true;
    return true;
  }

  /** Rotate a rotation-enabled piece 90° clockwise. */
  rotatePiece(slot: number): boolean {
    const piece = this.tray[slot];
    if (!piece || !piece.rotationEnabled || this.gameOver) return false;
    piece.rotation = (piece.rotation + 90) % 360;
    piece.cells = rotateCells(piece.cells, 1);
    return true;
  }

  // ---- undo ----
  undo(): boolean {
    if (!this.undoAvailable || !this.undoSnapshot) return false;
    this.loadSave(this.undoSnapshot);
    this.undoSnapshot = null;
    this.undoAvailable = false;
    return true;
  }

  // ---- game-over detection ----
  hasAnyValidMove(): boolean {
    for (const piece of this.tray) {
      if (!piece) continue;
      // A piece can be rotated for free if it's enabled, or could be (a point is available).
      const canRotate = piece.rotationEnabled || this.rotates > 0;
      const rotationsToTest = canRotate ? 4 : 1;
      let cells = piece.cells;
      for (let r = 0; r < rotationsToTest; r++) {
        const variant = r === 0 ? cells : rotateCells(cells, r);
        const { w, h } = cellsExtent(variant);
        for (let az = 0; az <= N - h; az++) {
          for (let ax = 0; ax <= N - w; ax++) {
            if (this.canPlaceCells(variant, ax, az)) return true;
          }
        }
      }
    }
    return false;
  }

  // ---- persistence ----
  toSave(): SaveState {
    return {
      version: 1,
      board: this.board.slice(),
      tray: this.tray.map((p) =>
        p
          ? ({
              shapeIndex: p.shapeIndex,
              rotation: p.rotation,
              value: p.value,
              rotationEnabled: p.rotationEnabled,
            } as SavedTrayPiece)
          : null,
      ),
      score: this.score,
      highScore: this.highScore,
      workingOnHighScore: this.workingOnHighScore,
      rotates: this.rotates,
      rotateCharge: this.rotateCharge,
      gameInProgress: this.gameInProgress,
      gameOver: this.gameOver,
    };
  }

  loadSave(state: SaveState): void {
    this.board = state.board.slice();
    this.tray = state.tray.map((p) =>
      p ? this.makePiece(p.shapeIndex, p.rotation, p.rotationEnabled) : null,
    );
    this.score = state.score;
    this.highScore = state.highScore;
    this.workingOnHighScore = state.workingOnHighScore;
    this.rotates = state.rotates;
    this.rotateCharge = state.rotateCharge;
    this.gameInProgress = state.gameInProgress;
    this.gameOver = state.gameOver;
    // A loaded game has no pending undo from this session.
    this.undoSnapshot = null;
    this.undoAvailable = false;
  }
}
